import type { OrderLineItem } from "@/types/order";
import type { Product } from "@/types/product";

/**
 * Pallet count rule (per business owner): one pallet holds 100 cases, and
 * leftover partials from different SKUs can be stacked together onto a
 * single mixed pallet. So total pallets = ceil(sum(qty) / 100) across all
 * non-empty line items. Returns 0 for empty/zero-case orders — callers
 * that need a "show at least one pallet" floor (BOL stamping) wrap with
 * `Math.max(1, ...)` locally. Audit 2026-06-16 / A-R3.
 */
export function palletsFromLineItems(lines: OrderLineItem[] | undefined | null): number {
  if (!lines || lines.length === 0) return 0;
  const totalCases = lines.reduce((sum, li) => {
    const q = Number(li.quantity) || 0;
    return q > 0 ? sum + q : sum;
  }, 0);
  if (totalCases <= 0) return 0;
  return Math.ceil(totalCases / 100);
}

/**
 * Statement pallet rule = prep-packet slip count.
 *
 * One pallet per 100 line-item cases, floor of 1 if the order has any
 * cases at all. This mirrors `Math.max(1, palletsFromLineItems(lines))`
 * used by `bol.functions.ts` to print pallet slips — the warehouse prints
 * exactly this many slips per order, so this is the authoritative count
 * displayed across the cash statement (on-screen + PDF) regardless of
 * payee kind. Audit 2026-06-16 / C-1.
 *
 * Returns 0 only when the order has no cases (voided / empty).
 */
export function palletsForStatement(
  lines: ReadonlyArray<{ quantity: number | null | undefined }> | undefined | null,
): number {
  if (!lines || lines.length === 0) return 0;
  const total = lines.reduce((s, li) => {
    const q = Number(li.quantity) || 0;
    return q > 0 ? s + q : s;
  }, 0);
  if (total <= 0) return 0;
  return Math.max(1, Math.ceil(total / 100));
}

/**
 * Pallet-slip contents block: one human-readable line per non-empty line
 * item, formatted as `{Product Name} · {oz}oz · {qty}cs`. Falls back to SKU
 * when the product is missing a name; drops the `· {oz}oz` segment when oz
 * is null. Returned in entry order. Empty result is the caller's problem
 * (BOL section renders a `BOL` placeholder if needed).
 */
export function lineItemContentLines(
  lines: OrderLineItem[] | undefined | null,
  products: Pick<Product, "id" | "oz" | "sku" | "name">[],
): string[] {
  if (!lines || lines.length === 0) return [];
  const byId = new Map(products.map((p) => [p.id, p]));
  return lines
    .filter((li) => li.productId && Number(li.quantity) > 0)
    .map((li) => {
      const p = byId.get(li.productId);
      const qty = Number(li.quantity) || 0;
      const name = p?.name?.trim() || p?.sku || "Item";
      const size = p?.oz != null ? ` · ${p.oz}oz` : "";
      return `${name}${size} · ${qty}cs`;
    });
}
